import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { associationsPatch } from '@collections/associations';
import { candidatesUpdate } from '@collections/candidates';
import { associationsClear } from '@modules/assigns/store';
import { NoteCategories, NoteSubject } from 'mojo-irp-notes';
import { notesCreate } from '@modules/notes/store';
import { SuccessAlert } from '@shared/alert';
import {
  getModal,
  isModalCancelled,
  isModalConfirmed,
  ModalShow,
  NoteModalShow,
} from '@shared/modal';
import {
  Association,
  Candidate,
  CandidateAdapter,
  CandidateBadges,
  DELETED_CANDIDATE,
} from '@shared/models';
import { AssociationService, CandidateAssociationService } from '@shared/services';
import { isTruthy } from '@shared/utils';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';

const modalTitle = 'Tell us why';

@Component({
  selector: 'mojo-candidate-badges-popover',
  templateUrl: './candidate-badges-popover.component.html',
  styleUrls: ['./candidate-badges-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateBadgesPopoverComponent implements OnInit, OnDestroy {
  private readonly DELETE_CANDIDATE_MODAL_TITLE = 'Delete Candidate';
  readonly deletedCandidate = DELETED_CANDIDATE;
  @Input() candidate: CandidateAdapter;
  @Input() isFullBadges: boolean;

  activeBadgesData = {};
  deactiveBadgesData = {};
  badges = CandidateBadges;

  cancelSubscription: Subscription;
  confirmSubscription: Subscription;

  isCancelled$ = this.store.pipe(select(isModalCancelled), filter(isTruthy));
  isConfirmed$ = this.store.pipe(select(isModalConfirmed), filter(isTruthy));

  subscription = new Subscription();

  alumniFeatureExtension = AppFeatureExtensions.ALUMNI_BADGE;

  constructor(
    private store: Store<{}>,
    private associationService: AssociationService,
    private candidateAssociationService: CandidateAssociationService,
  ) {}

  ngOnInit(): void {
    this.activeBadgesData[CandidateBadges.FAVORITE] = {
      noteCategory: NoteCategories.CandidateFavorite,
      modalTitle,
      modalSubTitle: `Let everyone know why ${this.candidate.name} is a Favorite.`,
      alertText: `${this.candidate.name} has been marketed as a Favorite.`,
    };
    this.activeBadgesData[CandidateBadges.SILVER] = {
      noteCategory: NoteCategories.CandidateSilver,
      modalTitle,
      modalSubTitle: `Let everyone know why ${this.candidate.name} is a Silver medalist.`,
      alertText: `${this.candidate.name} has been marked as a Silver Medalist.`,
    };
    this.activeBadgesData[CandidateBadges.ALUMNI] = {
      noteCategory: NoteCategories.CandidateAlumni,
      modalTitle,
      modalSubTitle: `Let everyone know why ${this.candidate.name} is an Alumni.`,
      alertText: `${this.candidate.name} has been marked as an Alumni.`,
    };
    this.activeBadgesData[CandidateBadges.WARNING] = {
      noteCategory: NoteCategories.CandidateWarning,
      modalTitle,
      modalSubTitle: `Let everyone know why ${this.candidate.name} is a Warning.`,
      alertText: `${this.candidate.name} has been marked as a Warning.`,
    };
    this.deactiveBadgesData[CandidateBadges.FAVORITE] = {
      noteCategory: NoteCategories.CandidateNotFavorite,
      modalTitle,
      modalSubTitle: ` Let everyone know why you don’t think ${this.candidate.name} is a Favorite anymore.`,
      alertText: `${this.candidate.name} is no longer marked as a Favorite.`,
    };
    this.deactiveBadgesData[CandidateBadges.SILVER] = {
      noteCategory: NoteCategories.CandidateNotSilver,
      modalTitle,
      modalSubTitle: ` Let everyone know why you don’t think ${this.candidate.name} is a Silver medalist anymore.`,
      alertText: `${this.candidate.name} is no longer marked as a Silver Medalist.`,
    };
    this.deactiveBadgesData[CandidateBadges.ALUMNI] = {
      noteCategory: NoteCategories.CandidateNotAlumni,
      modalTitle,
      modalSubTitle: ` Let everyone know why you don’t think ${this.candidate.name} is an Alumni anymore.`,
      alertText: `${this.candidate.name} is no longer marked as an Alumni.`,
    };
    this.deactiveBadgesData[CandidateBadges.WARNING] = {
      noteCategory: NoteCategories.CandidateNotWarning,
      modalTitle,
      modalSubTitle: ` Let everyone know why you don’t think ${this.candidate.name} is a Warning anymore.`,
      alertText: `${this.candidate.name} is no longer marked as a Warning.`,
    };
  }

  setBadge(badge: CandidateBadges) {
    const candidate = {
      id: this.candidate.id,
      favorite: this.candidate.favorite,
      silver: this.candidate.silver,
      alumni: this.candidate.alumni,
      warning: this.candidate.warning,
      socialInfo: this.candidate.socialInfo,
    };
    if (!this.candidate[badge]) {
      candidate[badge] = true;
      this.changeBadge(this.activeBadgesData, badge, candidate);
    } else {
      candidate[badge] = false;
      this.changeBadge(this.deactiveBadgesData, badge, candidate);
    }
  }

  private buildNote(category: NoteCategories) {
    return {
      subjectId: this.candidate.id,
      category,
      subjectType: NoteSubject.Candidate,
      systemGenerated: true,
    };
  }

  private changeBadge(badgesData: {}, badge: CandidateBadges, candidate: Partial<Candidate>) {
    if (this.isFullBadges) {
      this.store.dispatch(
        new NoteModalShow({
          title: badgesData[badge].modalTitle,
          subTitle: badgesData[badge].modalSubTitle,
          notes: [this.buildNote(badgesData[badge].noteCategory)],
        }),
      );

      this.confirmSubscription = this.isConfirmed$.subscribe(() => {
        this.unsubscribe();
        this.store.dispatch(candidatesUpdate({ payload: candidate }));
        this.store.dispatch(new SuccessAlert(badgesData[badge].alertText));
      });
      this.cancelSubscription = this.isCancelled$.subscribe(() => this.unsubscribe());
    } else {
      this.store.dispatch(candidatesUpdate({ payload: candidate }));
      this.store.dispatch(new SuccessAlert(badgesData[badge].alertText));
    }
  }

  private unsubscribe() {
    if (!!this.confirmSubscription) {
      this.confirmSubscription.unsubscribe();
    }
    if (!!this.cancelSubscription) {
      this.cancelSubscription.unsubscribe();
    }
  }

  deleteCandidate() {
    this.store.dispatch(
      new ModalShow({
        title: this.DELETE_CANDIDATE_MODAL_TITLE,
        subTitle:
          'This will remove the candidate from all applied and assigned jobs. ' +
          'All statuses and data related to this candidate will be deleted permanently. This can not be undone.',
        isCommentRequired: false,
        canDecline: true,
      }),
    );

    this.listenForDeleteCandidateModalConfirmation();
  }

  private static buildCandidateDeletedNote(subjectId: number, subjectType: NoteSubject) {
    return {
      content: '',
      subjectId,
      subjectType,
      category: NoteCategories.CandidateDeleted,
      systemGenerated: true,
      isPublic: true,
    };
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  listenForDeleteCandidateModalConfirmation() {
    this.subscription.add(
      this.store
        .select(getModal)
        .pipe(
          filter(
            (modal) =>
              modal.isConfirmed === true && modal.title === this.DELETE_CANDIDATE_MODAL_TITLE,
          ),
          switchMap(() =>
            this.candidateAssociationService.get({
              id: this.candidate.id + '/list',
              params: { assigned: true },
            }),
          ),
        )
        .subscribe((associations: Association[]) => {
          associations.forEach((association) => {
            this.store.dispatch(
              associationsPatch({
                association: {
                  id: association.id,
                  status: this.deletedCandidate,
                },
              }),
            );
            this.createCandidateDeletedNote(association.id, NoteSubject.Association);
          });
          this.createCandidateDeletedNote(this.candidate.id, NoteSubject.Candidate);
          this.store.dispatch(associationsClear());
          this.store.dispatch(
            candidatesUpdate({
              payload: {
                id: this.candidate.id,
                status: this.deletedCandidate,
              },
            }),
          );
        }),
    );
  }

  private createCandidateDeletedNote(subjectId: number, subjectType: NoteSubject) {
    this.store.dispatch(
      notesCreate({
        payload: CandidateBadgesPopoverComponent.buildCandidateDeletedNote(subjectId, subjectType),
      }),
    );
  }
}
